import React, {FC, ReactElement, useMemo} from 'react';
import {Card, Empty} from "antd";
import {DateFilter, DateFilterUnit} from "./DateFilter";
import {Mix, MixConfig} from "@ant-design/plots";
import {Translate, useLabels} from "../../../hooks/Labels";
import {useFilterPrefix} from "../../../components/DataTable/Filters/FilterPrefixProvider";
import moment from "moment";
import {useSearchParams} from "react-router-dom";
import {useQuerySearchParams} from "../../../config/queryClient";
import {QueryFilter} from "../../../components/DataTable/Filters/types";
import {SpinContainer} from "../../../components/SpinContainer";
import { StudentEngagementChartCardProps } from './StudentEngagementChartCard';


type ChartDatum = {
    start_time: string;
    average_score?: number;
    total?: number;
    [key: string]: any;
};

interface MixLineChartCardProps {
    title: React.ReactNode;
    extra?: React.ReactNode;
    isLoading: boolean;
    hasData: boolean;
    config: MixConfig;
}

const MixLineChartCard: FC<MixLineChartCardProps> = ({title, extra, isLoading, hasData, config}): ReactElement => (
    <Card title={title} className='chart-card' extra={extra}>
        {
            isLoading
                ? <SpinContainer/>
                : hasData
                    ? <Mix 
                        {...config} 
                        style={{height: 'stretch'}}
                    />
                    : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
        }
    </Card>
);

export const unitFormatShort: { [key in DateFilterUnit]: string } = {
    [DateFilterUnit.MONTH]: 'MMM YY',
    [DateFilterUnit.DAY]: 'D MMM',
    [DateFilterUnit.HOUR]: 'H:m',
    [DateFilterUnit.YEAR]: 'YYYY',
    [DateFilterUnit.WEEK]: '[Week] D MMM YYYY',
}

export const unitFormatLong: { [key in DateFilterUnit]: string } = {
    [DateFilterUnit.MONTH]: 'MMMM YYYY',
    [DateFilterUnit.DAY]: 'Do MMMM YYYY',
    [DateFilterUnit.HOUR]: 'H:m Do MMMM',
    [DateFilterUnit.YEAR]: '[Year] YYYY',
    [DateFilterUnit.WEEK]: '[Week] Do MMMM YYYY',
}

const createXAxisConfig = (unit: DateFilterUnit) => ({
    tickMethod: 'time-cat',
    label: {
        formatter: (text: string) =>
            moment(text).format(unitFormatShort[unit])
    }
});

const createMixChartConfig = (unit: DateFilterUnit, t: Translate, data: ChartDatum[]): MixConfig => {
    const formatTooltipTitle = (text: string) => moment(text).format(unitFormatLong[unit]);
    const formatTooltipItems = (items: any[]) => items.map(item => {
        console.log(item);
        if (item.name === 'average_score') {
            return {
                ...item,
                name: t('average_score_title'),
                value: `${item.value}%`
            };
        } else if (item.name === 'total') {
            return {
                ...item,
                name: `Completed ${t('activity_pl', false)}`,
                value: `${item.value}`
            };
        }
        return item;
    });

    return {
        padding: 'auto',
        syncViewPadding: true,
        tooltip: {
            shared: true,
            title: formatTooltipTitle,
            customItems: formatTooltipItems,
        },
        plots: [
            {
                type: 'line',
                region: {start: {x: 0, y: 0}, end: {x: 1, y: 0.6}},
                options: {
                    data,
                    xField: 'start_time',
                    yField: 'average_score',
                    color: '#5B8FF9',
                    xAxis: false,
                    yAxis: {
                        min: 0,
                        max: 100,
                        label: {
                            formatter: (text: string) => `${text}%`
                        }
                    },
                    point: {
                        size: 6
                    },
                    lineStyle: {
                        lineWidth: 2
                    },
                    appendPadding: [30, 15],
                    annotations: [
                        {
                            type: 'text',
                            content: t('average_score_title'), 
                            position: ['0%', '0%'],
                            offsetY: -25,
                        }
                    ],
                }
            },
            {
                type: 'column',
                region: {start: {x: 0, y: 0.6}, end: {x: 1, y: 1}},
                options: {
                    data,
                    xField: 'start_time',
                    yField: 'total',
                    color: '#5AD8A6',
                    xAxis: createXAxisConfig(unit),
                    yAxis: {
                        min: 0
                    },
                    minColumnWidth: 20,
                    maxColumnWidth: 30,
                    annotations: [
                        {
                            type: 'text',
                            content: `Completed ${t('activity_pl', false)}`, 
                            position: ['0%', '0%'],
                            offsetY: -25,
                        }
                    ],
                }
            }
        ]
    };
};

export const useEngagementStackedLineChartProps = (defaultFilters: StudentEngagementChartCardProps['defaultFilters']) => {
    const {t} = useLabels();
    const filterPrefix = useFilterPrefix();
    const [searchParams] = useSearchParams();

    const unit = useMemo<DateFilterUnit>(() => searchParams.get(`${filterPrefix}.unit`) as DateFilterUnit, [searchParams, filterPrefix]);

    const {data = [], isLoading} = useQuerySearchParams<any[]>(
        '/reports/student-engagement/', [filterPrefix, QueryFilter.COHORTS, QueryFilter.COURSES], {
            enabled: !!unit,
            defaultFilters
        });

    const chartData = useMemo<ChartDatum[]>(() => data.map(row => ({...row, ...row['time_period']})), [data]);
    const chartConfig = useMemo(() => createMixChartConfig(unit, t, chartData), [unit, t, chartData]);
    return {chartData, chartConfig, isLoading}
}

export const StudentEngagementStackedLineChartCard: FC<StudentEngagementChartCardProps> = ({title, defaultFilters, extra = <DateFilter property='created_at'/>}): ReactElement => {
    const {t} = useLabels();
    const {isLoading, chartConfig, chartData} = useEngagementStackedLineChartProps(defaultFilters);

    return (
        <MixLineChartCard
            title={title || `${t('student')} engagement`}
            extra={extra}
            isLoading={isLoading}
            hasData={chartData.length > 0}
            config={chartConfig}
        />
    );
};
